import {
  AudioPlaySVG,
  Forward10SecSVG,
  PauseButtonSVG,
  RecordButtonSVG,
  Rewind10SecSVG,
} from "@/utils/svg";
import { PauseCircle } from "lucide-react";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./button";
import Modal from "./Modal";

interface AudioRecordingModalProps {
  onClose: () => void;
  onSave: (file: File) => void;
  maxRecordingDurationSeconds?: number;
}

const AudioRecordingModal1: React.FC<AudioRecordingModalProps> = ({
  onClose,
  onSave,
  maxRecordingDurationSeconds = 300,
}) => {
  // State variables
  const [recordingTime, setRecordingTime] = useState(0);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // Audio playback controls
  const togglePlayback = useCallback(
    (audioElement: HTMLAudioElement | null) => {
      if (!audioElement) return;

      if (isPlaying) {
        audioElement.pause();
        setIsPlaying(false);

        // Clear playback timer when paused
        if (playbackIntervalRef.current) {
          clearInterval(playbackIntervalRef.current);
        }
      } else {
        audioElement.play();
        setIsPlaying(true);

        // Start playback timer
        playbackIntervalRef.current = setInterval(() => {
          if (audioElement) {
            setPlaybackTime(Math.floor(audioElement.currentTime));
          }
        }, 250);
      }
    },
    [isPlaying]
  );

  // Skip forward 10 seconds
  const skipForward = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(
      audioRef.current.duration,
      audioRef.current.currentTime + 10
    );
  }, []);

  // Rewind 10 seconds
  const rewind = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(
      0,
      audioRef.current.currentTime - 10
    );
  }, []);

  // Handle end of playback
  const handleEnded = useCallback(() => {
    setIsPlaying(false);

    // Clear playback timer when video ends
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
    }
  }, []);

  // Initialize audio stream and handle permissions
  useEffect(() => {
    // Get user media for audio
    const getAudioStream = async () => {
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        setStream(audioStream);
        setErrorMessage(null);
      } catch (err: any) {
        console.error("Error accessing microphone:", err);

        // Handle different error types
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          setErrorMessage(
            "Microphone access denied. Please allow microphone access in your browser settings."
          );
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          setErrorMessage(
            "No microphone found. Please connect a microphone and try again."
          );
        } else if (
          err.name === "NotReadableError" ||
          err.name === "TrackStartError"
        ) {
          setErrorMessage(
            "Microphone is being used by another application. Please close other applications using the microphone."
          );
        } else {
          setErrorMessage(
            `Could not access microphone: ${err.message || "Unknown error"}`
          );
        }
      }
    };

    getAudioStream();

    // Cleanup function
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, []);

  // Start recording function
  const startRecording = useCallback(() => {
    if (!stream) {
      setErrorMessage(
        "No microphone access. Please allow microphone access to record audio."
      );
      return;
    }

    try {
      // Reset recording state
      setRecordedChunks([]);
      setRecordingTime(0);
      setIsRecording(true);

      // Create MediaRecorder instance
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      mediaRecorderRef.current = mediaRecorder;

      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          setRecordedChunks((prev) => [...prev, event.data]);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setErrorMessage(
          "An error occurred during recording. Please try again."
        );
        stopRecording();
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      startTimeRef.current = Date.now();

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          // Auto-stop recording after maxRecordingDurationSeconds
          if (newTime >= maxRecordingDurationSeconds) {
            setTimeout(() => stopRecording(), 0);
            return maxRecordingDurationSeconds;
          }
          return newTime;
        });
      }, 1000);
    } catch (err: any) {
      console.error("Error starting recording:", err);
      setErrorMessage(
        `Could not start recording: ${err.message || "Unknown error"}`
      );
    }
  }, [stream, maxRecordingDurationSeconds]);

  // Stop recording function
  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current || !isRecording) return;

    try {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    } catch (err: any) {
      console.error("Error stopping recording:", err);
    }
  }, [isRecording]);

  // Create blob URL from recorded chunks when recording stops
  useEffect(() => {
    if (recordedChunks.length > 0 && !isRecording) {
      const blob = new Blob(recordedChunks, {
        type: "audio/webm",
      });
      const url = URL.createObjectURL(blob);
      setMediaBlobUrl(url);

      // Reset playback time when creating a new recording
      setPlaybackTime(0);
    }
  }, [recordedChunks, isRecording]);

  // Handle save button click
  const handleSave = useCallback(() => {
    if (!mediaBlobUrl && recordedChunks.length === 0) return;

    const blob = new Blob(recordedChunks, {
      type: "audio/webm",
    });

    const fileName = `audio-recording-${Date.now()}.webm`;
    const fileType = "audio/webm";
    const audioFile = new File([blob], fileName, { type: fileType });

    onSave(audioFile);
    onClose();
  }, [mediaBlobUrl, recordedChunks, onSave, onClose]);

  // Set audio source when mediaBlobUrl changes
  useEffect(() => {
    if (audioRef.current && mediaBlobUrl) {
      audioRef.current.src = mediaBlobUrl;
    }
  }, [mediaBlobUrl]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Stop all tracks in the stream
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      // Clear timers
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }

      // Revoke any created object URLs
      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
    };
  }, [stream, mediaBlobUrl]);

  // Listen for audio playback ended event
  useEffect(() => {
    const audioElement = audioRef.current;

    if (audioElement) {
      audioElement.addEventListener("ended", handleEnded);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("ended", handleEnded);
      }
    };
  }, [handleEnded]);

  return (
    <>
      <div className="p-6 flex flex-col items-center">
        <h2 className="text-2xl text-[#1F1F1F] font-semibold mb-[43px] self-start">
          Record Audio{" "}
        </h2>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {errorMessage}
          </div>
        )}

        {mediaBlobUrl && !isRecording ? (
          <div className="w-full mb-[12px] flex flex-col items-center justify-center gap-2">
            <audio
              ref={audioRef}
              src={mediaBlobUrl}
              controls={false}
              onEnded={handleEnded}
              onTimeUpdate={(e) =>
                setPlaybackTime(Math.floor(e.currentTarget.currentTime))
              }
              className="w-full hidden"
            />

            <div className="w-full flex items-center justify-center gap-2">
              {/* Time indicator - current time */}
              <div className="flex w-[60px] text-right font-semibold">
                <span>{formatTime(playbackTime)}</span>
              </div>

              {/* Audio progress bar */}
              <div className="w-full h-[5px] bg-gray-100 rounded-md relative overflow-hidden h-3">
                {audioRef.current && (
                  <div
                    className="bg-[#FFE000] absolute left-0 top-0"
                    style={{
                      width: `${
                        audioRef.current.duration
                          ? (playbackTime / audioRef.current.duration) * 100
                          : 0
                      }%`,
                    }}
                  />
                )}
              </div>

              {/* Time indicator - remaining time */}
              <div className="flex justify-between w-[60px] font-semibold">
                {audioRef.current ? (
                  <span>-{formatTime(maxRecordingDurationSeconds)}</span>
                ) : (
                  <span>-00:00</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full flex items-center justify-center relative mb-[12px] gap-2">
            {/* Time indicators */}
            <div className="flex w-[60px] text-right font-semibold">
              {isRecording ? (
                <span>{formatTime(recordingTime)}</span>
              ) : mediaBlobUrl && isPlaying ? (
                <span>{formatTime(playbackTime)}</span>
              ) : (
                <span>00:00</span>
              )}
            </div>

            {/* Audio visualization */}
            <div className="w-full h-[5px] bg-gray-100 rounded-md relative overflow-hidden h-3">
              {isRecording ? (
                <div
                  className="h-full bg-[#FFE000] absolute left-0 top-0"
                  style={{
                    width: `${
                      (recordingTime / maxRecordingDurationSeconds) * 100
                    }%`,
                  }}
                />
              ) : null}
            </div>
            {/* Time indicators */}
            <div className="flex justify-between w-[60px] font-semibold">
              {isRecording ? (
                <span>
                  -
                  {formatTime(
                    Math.max(0, maxRecordingDurationSeconds - recordingTime)
                  )}
                </span>
              ) : (
                <span>-{formatTime(maxRecordingDurationSeconds)}</span>
              )}
            </div>
          </div>
        )}

        {/* Record/Play button with rewind/forward controls */}
        <div className="mb-[12px] flex items-center justify-center gap-4">
          {mediaBlobUrl && !isRecording ? (
            <>
              <div className="cursor-pointer" onClick={rewind}>
                <Rewind10SecSVG />
              </div>
              <div
                className="cursor-pointer"
                onClick={() => togglePlayback(audioRef.current)}
              >
                {isPlaying ? (
                  <PauseCircle className="w-[48px] h-[48px] ml-1 text-[#C53921]" />
                ) : (
                  // <PlayCircle className="w-8 h-8 ml-1 text-[#2165C5]" />
                  <AudioPlaySVG />
                )}
              </div>
              <div className="cursor-pointer" onClick={skipForward}>
                <Forward10SecSVG />
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              className="w-16 h-16 rounded-full flex items-center justify-center"
              disabled={!stream}
            >
              {isRecording ? <PauseButtonSVG /> : <RecordButtonSVG />}
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
        <Button
          onClick={() => setShowConfirmCancelModal(true)}
          variant="light"
          size="md"
          type="button"
          className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          type="button"
          onClick={handleSave}
          disabled={!mediaBlobUrl || isRecording}
          className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
        >
          Upload
        </Button>
      </div>

      <Modal
        isOpen={showConfirmCancelModal}
        onClose={() => setShowConfirmCancelModal(false)}
        title="Leave"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] text-center mb-8">
            Are you sure you want to exit the recording,
            <br />
            {mediaBlobUrl && " Your recording will be lost"}
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setShowConfirmCancelModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => {
              setShowConfirmCancelModal(false);
              onClose();
            }}
          >
            Yes, Cancel
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AudioRecordingModal1;
